/** Authoritative add/update path for the custom product and collection controls. */
(() => {
  if (window.RaidageCart) return;
  const pending = new WeakSet();
  const confirmationError = 'Unable to confirm this add. Check your cart before trying again.';

  function jsonRoute(route) {
    if (!route) throw new Error('Cart is unavailable. Please reload and try again.');
    const url = new URL(route, window.location.origin);
    if (!url.pathname.endsWith('.js')) url.pathname += '.js';
    return url.pathname + url.search;
  }

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      let data;
      try { data = await response.json(); } catch (_) { throw new Error(confirmationError); }
      if (!response.ok || (data.status && Number(data.status) >= 400) || data.errors) {
        const details = typeof data.description === 'string' ? data.description : typeof data.errors === 'string' ? data.errors : data.message;
        throw new Error(details || window.cartStrings?.error || 'The item could not be added. Please try again.');
      }
      return data;
    } finally { clearTimeout(timer); }
  }

  async function add(variantId, trigger) {
    if (!/^\d+$/.test(String(variantId))) throw new Error('Please select an available option.');
    if (trigger && pending.has(trigger)) throw new Error('This item is already being added.');
    if (trigger) pending.add(trigger);
    try {
      const feedback = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sectionIds = feedback?.getSectionsToRender().map(section => section.id) || [];
      const added = await request(jsonRoute(window.routes?.cart_add_url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: 1, ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}) }),
      });
      if (!added.id || !added.key) throw new Error(confirmationError);
      let cart;
      let warning = '';
      try {
        cart = await request(jsonRoute(window.routes?.cart_url));
        if (!Number.isInteger(cart.item_count)) throw new Error('Invalid cart state');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (_) { warning = 'Added to cart, but the cart display could not refresh. Please view your cart.'; }
      if (feedback) {
        try {
          let sections = added.sections || {};
          if (sectionIds.some(id => typeof sections[id] !== 'string')) {
            const url = new URL(window.routes.cart_url, window.location.origin);
            url.searchParams.set('sections', sectionIds.join(','));
            sections = await request(url.pathname + url.search);
          }
          if (sectionIds.some(id => typeof sections[id] !== 'string')) throw new Error('Cart feedback unavailable');
          feedback.setActiveElement(trigger);
          feedback.classList.remove('is-empty');
          feedback.renderContents({ ...added, sections });
        } catch (_) { warning = 'Added to cart, but the cart display could not refresh. Please view your cart.'; }
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'raidage-custom-add', productVariantId: String(variantId), cartData: cart || added });
        } catch (_) { warning = 'Added to cart, but the cart display could not refresh. Please view your cart.'; }
      }
      return { added, cart, warning };
    } catch (error) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        try {
          await publish(PUB_SUB_EVENTS.cartError, { source: 'raidage-custom-add', productVariantId: String(variantId), message: error.message });
        } catch (_) { /* An optional subscriber must not replace the request error. */ }
      }
      if (error.name === 'AbortError' || error instanceof TypeError) throw new Error(confirmationError);
      throw error;
    } finally { if (trigger) pending.delete(trigger); }
  }
  window.RaidageCart = Object.freeze({ add });
})();
